import React, { useRef, useState, useCallback, useEffect } from 'react';
import { ZoomIn, ZoomOut, Maximize, FileText, Paperclip } from 'lucide-react';
import { NODE_W, NODE_H, V_SEP } from './useOrgChartLayout';

const LEVEL_COLORS = {
  0: '#2f5be8', 1: '#4a72f0', 2: '#0ab394', 3: '#6340d4',
  4: '#1a9ec0', 5: '#4a9020', 6: '#9098c0',
};

function getInitials(name) {
  if (!name || name === 'Vacante') return 'V';
  return name.split(/[\s/]+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

export const OrgChartCanvas = ({
  nodes, positions, connections, levelLines, bounds,
  visibleNodeIds, selectedNodeId, onSelectNode, onDeselectNode,
  activeFilter, isEditMode, onEditNode, onDeleteNode,
}) => {
  const containerRef = useRef(null);
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
  const didDragRef = useRef(false);
  const DRAG_THRESHOLD = 5;
  const [tooltip, setTooltip] = useState(null);

  // Fit to screen on mount
  useEffect(() => {
    if (bounds.width > 0 && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const padding = 60;
      const scaleX = (rect.width - padding * 2) / bounds.width;
      const scaleY = (rect.height - padding * 2) / bounds.height;
      const scale = Math.min(scaleX, scaleY, 1);
      const x = (rect.width - bounds.width * scale) / 2;
      const y = (rect.height - bounds.height * scale) / 2;
      setTransform({ x, y, scale });
    }
  }, [bounds]);

  const fitToScreen = useCallback(() => {
    if (!containerRef.current || bounds.width === 0) return;
    const rect = containerRef.current.getBoundingClientRect();
    const padding = 60;
    const scaleX = (rect.width - padding * 2) / bounds.width;
    const scaleY = (rect.height - padding * 2) / bounds.height;
    const scale = Math.min(scaleX, scaleY, 1);
    const x = (rect.width - bounds.width * scale) / 2;
    const y = (rect.height - bounds.height * scale) / 2;
    setTransform({ x, y, scale });
  }, [bounds]);

  // Center on a specific node
  const centerOnNode = useCallback((nodeId) => {
    const pos = positions[nodeId];
    if (!pos || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const cx = pos.x + NODE_W / 2;
    const cy = pos.y + NODE_H / 2;
    const scale = Math.max(transform.scale, 0.6);
    setTransform({
      x: rect.width / 2 - cx * scale,
      y: rect.height / 2 - cy * scale,
      scale,
    });
  }, [positions, transform.scale]);

  // Expose centerOnNode via ref-like pattern
  useEffect(() => {
    if (selectedNodeId && positions[selectedNodeId]) {
      centerOnNode(selectedNodeId);
    }
  }, [selectedNodeId]);

  // Pan handlers
  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    if (e.target.closest('[data-orgnode]')) return;
    setIsPanning(true);
    didDragRef.current = false;
    panStartRef.current = { x: e.clientX, y: e.clientY, tx: transform.x, ty: transform.y };
  }, [transform.x, transform.y]);

  const handleMouseMove = useCallback((e) => {
    if (!isPanning) return;
    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
      didDragRef.current = true;
    }
    setTransform(prev => ({ ...prev, x: panStartRef.current.tx + dx, y: panStartRef.current.ty + dy }));
  }, [isPanning]);

  const handleMouseUp = useCallback(() => {
    if (isPanning) {
      // Only deselect on a true click (no drag), not on a pan
      if (!didDragRef.current) {
        onDeselectNode();
      }
      setIsPanning(false);
    }
  }, [isPanning, onDeselectNode]);

  // Zoom with wheel
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setTransform(prev => {
      const newScale = Math.min(Math.max(prev.scale * delta, 0.1), 3);
      const ratio = newScale / prev.scale;
      return {
        scale: newScale,
        x: mouseX - (mouseX - prev.x) * ratio,
        y: mouseY - (mouseY - prev.y) * ratio,
      };
    });
  }, []);

  const zoom = useCallback((factor) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    setTransform(prev => {
      const newScale = Math.min(Math.max(prev.scale * factor, 0.1), 3);
      const ratio = newScale / prev.scale;
      return { scale: newScale, x: cx - (cx - prev.x) * ratio, y: cy - (cy - prev.y) * ratio };
    });
  }, []);

  // Determine which nodes to highlight
  const selectedNode = nodes.find(n => n.node_id === selectedNodeId);
  const highlightSet = new Set();
  if (selectedNode) {
    highlightSet.add(selectedNode.node_id);
    if (selectedNode.parent_id) highlightSet.add(selectedNode.parent_id);
    nodes.forEach(n => { if (n.parent_id === selectedNode.node_id) highlightSet.add(n.node_id); });
  }

  // Filter logic: visibleNodeIds is set when layout computed a filtered subset
  const isLevelFiltered = visibleNodeIds !== null;

  // For 'vacant' filter (no layout change), dim non-vacant
  const isVacantFilter = activeFilter === 'vacant';

  // Minimap: show only visible nodes when level-filtered
  const minimapW = 160;
  const minimapH = 100;
  const miniScale = bounds.width > 0 ? Math.min(minimapW / bounds.width, minimapH / bounds.height) : 1;

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full overflow-hidden bg-slate-50 select-none"
      style={{ cursor: isPanning ? 'grabbing' : 'grab' }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheel}
      data-testid="orgchart-canvas"
    >
      <svg
        width="100%"
        height="100%"
        style={{ position: 'absolute', top: 0, left: 0 }}
      >
        <g transform={`translate(${transform.x}, ${transform.y}) scale(${transform.scale})`}>
          {/* Level separator lines */}
          {levelLines.map(line => (
            <g key={`level-${line.level}`}>
              <line
                x1={-40} y1={line.y} x2={line.width} y2={line.y}
                stroke="#e2e8f0" strokeWidth={1} strokeDasharray="6 4" opacity={0.7}
              />
              <text x={-35} y={line.y + 4} fontSize={10} fill="#94a3b8" fontWeight="600">{line.label}</text>
            </g>
          ))}

          {/* Connections — hidden when level filter is active */}
          {!isLevelFiltered && connections.map((conn, i) => {
            let opacity = 1;
            let visible = true;

            if (selectedNodeId) {
              // Only show connections directly linking selected node to its parent or children
              const isFromSelected = conn.parentId === selectedNodeId;
              const isToSelected = conn.parentId === selectedNode?.parent_id && (
                conn.type === 'parent-bus' ||
                conn.type === 'bus' ||
                (conn.type === 'bus-child' && conn.childId === selectedNodeId)
              );
              visible = isFromSelected || isToSelected;
            }

            if (isVacantFilter) opacity = 0.15;
            if (!visible) return null;

            return (
              <line
                key={i}
                x1={conn.x1} y1={conn.y1} x2={conn.x2} y2={conn.y2}
                stroke="#94a3b8" strokeWidth={1.5} opacity={opacity}
                style={{ transition: 'opacity 0.3s' }}
              />
            );
          })}

          {/* Nodes */}
          {nodes.map(node => {
            const pos = positions[node.node_id];
            if (!pos) return null;
            // When level filter is active, only render visible nodes
            if (isLevelFiltered && !visibleNodeIds.has(node.node_id)) return null;

            const isVacant = node.nombre === 'Vacante';
            const color = isVacant ? '#d93050' : (LEVEL_COLORS[node.level] || '#9098c0');
            const isSelected = selectedNodeId === node.node_id;
            const isHighlighted = highlightSet.has(node.node_id);
            const hasDescription = node.description && node.description.trim().length > 0;
            const hasAttachments = node.attachments && node.attachments.length > 0;

            let opacity = 1;
            if (selectedNodeId && !isHighlighted) opacity = 0.2;
            if (isVacantFilter && node.nombre !== 'Vacante') opacity = 0.15;

            const childCount = nodes.filter(n => n.parent_id === node.node_id).length;

            return (
              <g
                key={node.node_id}
                data-orgnode={node.node_id}
                transform={`translate(${pos.x}, ${pos.y})`}
                style={{ cursor: 'pointer', transition: 'opacity 0.3s' }}
                opacity={opacity}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isEditMode) {
                    onEditNode(node);
                  } else {
                    onSelectNode(node.node_id);
                  }
                }}
                onMouseEnter={(e) => {
                  const rect = containerRef.current?.getBoundingClientRect();
                  if (rect) {
                    setTooltip({
                      text: `${node.puesto} | ${childCount} subordinado(s)`,
                      x: e.clientX - rect.left,
                      y: e.clientY - rect.top - 40,
                    });
                  }
                }}
                onMouseLeave={() => setTooltip(null)}
              >
                {/* Card shadow */}
                <rect x={2} y={2} width={NODE_W} height={NODE_H} rx={10} fill="rgba(0,0,0,0.06)" />
                {/* Card body */}
                <rect
                  x={0} y={0} width={NODE_W} height={NODE_H} rx={10}
                  fill="white" stroke={isSelected ? color : '#e2e8f0'}
                  strokeWidth={isSelected ? 2.5 : 1}
                />
                {/* Left color accent */}
                <rect x={0} y={0} width={5} height={NODE_H} rx={3} fill={color} />
                {/* Avatar circle */}
                <circle cx={30} cy={NODE_H / 2} r={16} fill={color} opacity={0.9} />
                <text x={30} y={NODE_H / 2 + 1} textAnchor="middle" dominantBaseline="central"
                  fontSize={11} fontWeight="bold" fill="white"
                >
                  {getInitials(node.nombre)}
                </text>
                {/* Labels */}
                <text x={52} y={24} fontSize={11} fontWeight="600" fill="#1e293b">
                  {node.puesto.length > 24 ? node.puesto.substring(0, 23) + '...' : node.puesto}
                </text>
                <text x={52} y={40} fontSize={10} fill={isVacant ? '#d93050' : '#64748b'} fontWeight={isVacant ? '600' : '400'}>
                  {node.nombre.length > 26 ? node.nombre.substring(0, 25) + '...' : node.nombre}
                </text>
                {/* Indicators */}
                {(hasDescription || hasAttachments) && (
                  <g transform={`translate(${NODE_W - 22}, ${NODE_H - 18})`}>
                    {hasDescription && <circle cx={0} cy={0} r={5} fill="#3b82f6" opacity={0.8} />}
                    {hasAttachments && <circle cx={hasDescription ? 12 : 0} cy={0} r={5} fill="#f59e0b" opacity={0.8} />}
                  </g>
                )}
                {/* Delete button in edit mode */}
                {isEditMode && (
                  <g
                    transform={`translate(${NODE_W - 12}, -6)`}
                    onClick={(e) => { e.stopPropagation(); onDeleteNode(node); }}
                    style={{ cursor: 'pointer' }}
                  >
                    <circle cx={0} cy={0} r={10} fill="#ef4444" stroke="white" strokeWidth={2} />
                    <text x={0} y={1} textAnchor="middle" dominantBaseline="central" fontSize={12} fill="white" fontWeight="bold">x</text>
                  </g>
                )}
              </g>
            );
          })}
        </g>
      </svg>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="absolute pointer-events-none bg-slate-900 text-white text-xs rounded-lg px-3 py-1.5 shadow-lg z-40 whitespace-nowrap"
          style={{ left: tooltip.x, top: tooltip.y, transform: 'translateX(-50%)' }}
          data-testid="orgchart-tooltip"
        >
          {tooltip.text}
        </div>
      )}

      {/* Zoom controls */}
      <div className="absolute top-3 right-3 flex flex-col gap-1 z-30" data-testid="orgchart-zoom-controls">
        <button onClick={() => zoom(1.2)} className="w-8 h-8 rounded-lg bg-white border border-slate-200 shadow-sm flex items-center justify-center hover:bg-slate-50 transition-colors" data-testid="orgchart-zoom-in">
          <ZoomIn className="w-4 h-4 text-slate-600" />
        </button>
        <button onClick={() => zoom(0.8)} className="w-8 h-8 rounded-lg bg-white border border-slate-200 shadow-sm flex items-center justify-center hover:bg-slate-50 transition-colors" data-testid="orgchart-zoom-out">
          <ZoomOut className="w-4 h-4 text-slate-600" />
        </button>
        <button onClick={fitToScreen} className="w-8 h-8 rounded-lg bg-white border border-slate-200 shadow-sm flex items-center justify-center hover:bg-slate-50 transition-colors" data-testid="orgchart-fit-screen">
          <Maximize className="w-4 h-4 text-slate-600" />
        </button>
      </div>

      {/* Minimap */}
      <div className="absolute bottom-3 left-3 bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden z-30" data-testid="orgchart-minimap">
        <svg width={minimapW} height={minimapH}>
          <rect width={minimapW} height={minimapH} fill="#f8fafc" />
          {nodes.map(node => {
            const pos = positions[node.node_id];
            if (!pos) return null;
            if (isLevelFiltered && !visibleNodeIds.has(node.node_id)) return null;
            const isVacant = node.nombre === 'Vacante';
            const color = isVacant ? '#d93050' : (LEVEL_COLORS[node.level] || '#9098c0');
            return (
              <rect
                key={node.node_id}
                x={pos.x * miniScale + 4}
                y={pos.y * miniScale + 4}
                width={Math.max(NODE_W * miniScale, 3)}
                height={Math.max(NODE_H * miniScale, 2)}
                fill={color}
                rx={1}
                opacity={selectedNodeId && !highlightSet.has(node.node_id) ? 0.2 : 0.8}
              />
            );
          })}
          {/* Viewport indicator */}
          {containerRef.current && bounds.width > 0 && (
            <rect
              x={(-transform.x / transform.scale) * miniScale + 4}
              y={(-transform.y / transform.scale) * miniScale + 4}
              width={(containerRef.current.clientWidth / transform.scale) * miniScale}
              height={(containerRef.current.clientHeight / transform.scale) * miniScale}
              fill="none" stroke="#3b82f6" strokeWidth={1.5} rx={1}
            />
          )}
        </svg>
      </div>

      {/* Scale indicator */}
      <div className="absolute bottom-3 right-3 bg-white border border-slate-200 rounded px-2 py-0.5 text-xs text-slate-500 z-30">
        {Math.round(transform.scale * 100)}%
      </div>
    </div>
  );
};


